import { validateIsExactText } from "common/utils/formValidators";
import normalizeSpaces from "common/utils/normalizeSpaces";
import type { FormEvent, ReactNode } from "react";
import { useState } from "react";
import Button from "ui/buttons/Button";
import Modal from "ui/components/Modal";
import InputGroupText from "ui/forms/InputGroupText";
import { Message } from "ui/text/Message";
import styles from "./ConfirmDeleteModal.module.scss";

interface ConfirmDeleteModalProps {
  onClose: () => any;
  onConfirm: (e: FormEvent<HTMLFormElement>) => Promise<void> | void;
  entityName?: string;
  entityType: string;
  message: ReactNode;
  isDeleting?: boolean;
  requireEntityNameEntry?: boolean;
  canRevert?: boolean;
  isMany?: boolean;
}

export default function ConfirmDeleteModal({
  onClose,
  onConfirm,
  entityName,
  entityType,
  message,
  isDeleting,
  requireEntityNameEntry = true,
  canRevert = false,
  isMany,
}: ConfirmDeleteModalProps) {
  const [confirmText, setConfirmText] = useState<string>("");

  const normalizedEntityName = entityName ? normalizeSpaces(entityName) : "";

  const isDisabled = () => {
    if (requireEntityNameEntry) {
      return validateIsExactText(confirmText, normalizedEntityName, true);
    } else {
      return null;
    }
  };
  const handleClose = () => {
    setConfirmText("");
    onClose();
  };

  return (
    <Modal
      open
      onClose={handleClose}
      size="small"
      title={`Delete ${entityType}`}
      footer={
        <>
          <Button btnSize="large" type="button" btnStyle="tertiary" onClick={handleClose}>
            Cancel
          </Button>
          <Button btnSize="large" type="submit" disabled={isDeleting || !!isDisabled()} form="confirm-delete-modal">
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </>
      }
      variants={["bodyPadding", "footerButtons"]}
    >
      <form id="confirm-delete-modal" className={styles.wrapper} onSubmit={onConfirm}>
        {!canRevert && (
          <Message variant="inverted" messageType="warning" marginBottom>
            Warning: this action is irreversible.
          </Message>
        )}
        {message}
        {requireEntityNameEntry && (
          <div className="confirmation">
            <p>
              To delete {!isMany ? "this" : "these"} {entityType}, type <b>{normalizedEntityName}</b> in the field
              below:
            </p>
            <div className="confirmation-input">
              <InputGroupText
                type="text"
                labelText="Confirm"
                inputValue={confirmText ?? ""}
                placeholder={normalizedEntityName}
                onChange={(value: string) => setConfirmText(value)}
                validate={() => isDisabled()}
              />
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}
